import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ReferralListItem } from "./ReferralListItem";
import { EmptyReferralsState } from "./EmptyReferralsState";
import { ReferralStatus } from "@/lib/schema";

interface ReferralListing {
  id: string;
  candidateName: string;
  jobTitle: string;
  status: ReferralStatus;
}

interface ReferralsSectionProps {
  referrals: ReferralListing[];
  isLoading?: boolean;
  title?: string;
  viewAllHref?: string;
  viewAllText?: string;
  maxReferrals?: number;
  className?: string;
  testId?: string;
}

export function ReferralsSection({ 
  referrals, 
  isLoading = false,
  title = "Your referrals",
  viewAllHref = "/referrals",
  viewAllText = "View all referrals",
  maxReferrals = 4,
  className = "",
  testId = "card-your-referrals"
}: ReferralsSectionProps) {
  const displayReferrals = referrals.slice(0, maxReferrals);

  return (
    <Card className={className} data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <Link href={viewAllHref}>
            <Button 
              variant="link" 
              className="text-orange-500 hover:text-orange-600 space-grotesk font-extrabold" 
              data-testid="link-view-all-referrals"
            >
              {viewAllText}
            </Button>
          </Link>
        </div>
        <div className="space-y-0">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: maxReferrals }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="flex justify-between items-center p-4">
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : displayReferrals.length > 0 ? (
            displayReferrals.map((referral: ReferralListing, index: number) => (
              <ReferralListItem key={referral.id || index} referral={referral} index={index} />
            ))
          ) : (
            <EmptyReferralsState />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
